import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Shield, ShieldCheck, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { api, type Agent } from "@/lib/api";

export default function AgentVerification() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [enrollmentToken, setEnrollmentToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const { data: agent, isLoading } = useQuery<Agent>({
    queryKey: ["agent", id],
    queryFn: async () => {
      const { data } = await api.get(`/agents/${id}`);
      return data;
    },
    enabled: !!id,
  });

  const challengeMutation = useMutation({
    mutationFn: async () => {
      const { data } = await api.post(`/agents/${id}/verification/challenge`);
      return data;
    },
    onSuccess: (data) => {
      setEnrollmentToken(data.enrollmentToken);
      toast.success("Enrollment token generated (expires in 10 minutes)");
    },
    onError: () => toast.error("Failed to generate challenge"),
  });

  const approveMutation = useMutation({
    mutationFn: async () => {
      const { data } = await api.post(`/agents/${id}/verification/approve`);
      return data;
    },
    onSuccess: () => {
      toast.success("Agent approved and activated!");
      void queryClient.invalidateQueries({ queryKey: ["agent", id] });
    },
    onError: () => toast.error("Failed to approve agent"),
  });

  const copyToken = async () => {
    if (enrollmentToken) {
      await navigator.clipboard.writeText(enrollmentToken);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!agent) {
    return <div className="text-center py-20 text-muted-foreground">Agent not found</div>;
  }

  const statusSteps = [
    { status: "PENDING_PROOF", label: "Awaiting Proof", step: 1 },
    { status: "PENDING_APPROVAL", label: "Awaiting Approval", step: 2 },
    { status: "ACTIVE", label: "Active", step: 3 },
  ];

  const currentStep = statusSteps.find((s) => s.status === agent.status)?.step ?? 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-display">{agent.name}</h1>
          <p className="text-muted-foreground font-mono text-sm">@{agent.id}</p>
        </div>
        <Badge variant={agent.status === "ACTIVE" ? "default" : "secondary"}>
          {agent.status.replace(/_/g, " ")}
        </Badge>
      </div>

      {/* Progress Steps */}
      <div className="flex gap-2">
        {statusSteps.map((step) => (
          <div
            key={step.status}
            className={`flex-1 h-2 rounded-full ${
              currentStep >= step.step ? "bg-primary" : "bg-muted"
            }`}
          />
        ))}
      </div>

      {agent.status === "PENDING_PROOF" && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              <CardTitle>Step 1: Generate Enrollment Token</CardTitle>
            </div>
            <CardDescription>
              Generate a short-lived token, then have your agent call the proof endpoint with it.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              onClick={() => challengeMutation.mutate()}
              disabled={challengeMutation.isPending}
            >
              {challengeMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Generate Enrollment Token
            </Button>

            {enrollmentToken && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Have your agent POST to <code className="text-xs bg-muted px-1 py-0.5 rounded">/v1/agents/{id}/verification/proof</code> with this token:
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs bg-muted p-3 rounded-md overflow-x-auto break-all">
                    {enrollmentToken}
                  </code>
                  <Button variant="outline" size="icon" onClick={copyToken}>
                    {copied ? <CheckCircle2 className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {agent.status === "PENDING_APPROVAL" && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" />
              <CardTitle>Step 2: Approve Agent</CardTitle>
            </div>
            <CardDescription>
              Your agent has submitted proof of operation. Review and approve to activate it.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => approveMutation.mutate()}
              disabled={approveMutation.isPending}
            >
              {approveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Approve & Activate Agent
            </Button>
          </CardContent>
        </Card>
      )}

      {agent.status === "ACTIVE" && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-green-500" />
              <CardTitle>Agent Verified</CardTitle>
            </div>
            <CardDescription>
              Your agent is verified and active. You can now create API keys for it.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate(`/agents/${id}/api-keys`)}>
              Manage API Keys
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
